import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { assetUrl, formatBytes, formatPrice, errMsg } from '@/lib/digitalApi';
import ProductEditor from '@/admin/digital/ProductEditor';
import CategoriesPanel from '@/admin/digital/CategoriesPanel';
import OrdersPanel from '@/admin/digital/OrdersPanel';
import EmailPreviewPanel from '@/admin/digital/EmailPreviewPanel';
import { SECTION_OPTIONS } from '@/admin/digital/ResourceFields';

const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const STATUS_CLS = { published: 'text-emerald-700', draft: 'text-[#888]', unpublished: 'text-amber-700' };

const Tab = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-xs tracking-[0.14em] uppercase px-4 py-1.5 rounded-full border transition-colors ${active ? 'bg-[#111] text-[#FAF8F5] border-[#111]' : 'bg-white text-[#555] border-[#eae4d8] hover:border-[#111]'}`}>
    {children}
  </button>
);

const ProductsTable = ({ items, onOpen }) => (
  <div className="border border-[#eae4d8] rounded-xl overflow-x-auto bg-white">
    <table className="w-full text-sm min-w-[900px]">
      <thead className="bg-[#FAF8F5] text-[#888] text-[11px] tracking-[0.14em] uppercase">
        <tr>{['', 'Title', 'Type', 'Sections', 'Category', 'Price', 'File', 'Status', 'Orders'].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}</tr>
      </thead>
      <tbody>
        {items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#888]" data-testid="dp-admin-empty">Nothing here yet. Click “New Resource” to add your first.</td></tr>}
        {items.map((p) => (
          <tr key={p.id} onClick={() => onOpen(p.id)} className="border-t border-[#eae4d8] hover:bg-[#FAF8F5] cursor-pointer" data-testid={`dp-admin-row-${p.slug}`}>
            <td className="px-4 py-3 w-14">
              {p.cover_url ? <img src={assetUrl(p.cover_url)} alt="" className="w-10 h-12 object-cover rounded" /> : <div className="w-10 h-12 rounded bg-[#f1ece3]" />}
            </td>
            <td className="px-4 py-3 text-[#111]" style={serif}><span className="text-lg">{p.title}</span></td>
            <td className="px-4 py-3 text-[#555]">{p.content_type === 'article' ? 'Article' : (p.format || 'Download')}{p.unlisted ? ' · Hidden' : ''}</td>
            <td className="px-4 py-3 text-xs text-[#555]">{(p.sections || []).map((s) => SECTION_OPTIONS.find(([k]) => k === s)?.[1]).join(', ')}</td>
            <td className="px-4 py-3 text-[#555]">{p.category_name || '—'}</td>
            <td className="px-4 py-3 text-[#111]">{p.content_type === 'article' && !p.file ? '—' : formatPrice(p.price_cents, p.is_free)}</td>
            <td className="px-4 py-3 text-xs text-[#555]">{p.file ? p.file.filename : (p.content_type === 'article' ? '—' : <span className="text-red-600">Missing</span>)}</td>
            <td className={`px-4 py-3 text-xs uppercase tracking-[0.12em] ${STATUS_CLS[p.status]}`}>{p.status}</td>
            <td className="px-4 py-3 text-[#555]">{p.order_count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function DigitalProductsAdmin() {
  const [tab, setTab] = useState('products');
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [section, setSection] = useState('all');

  const load = async () => {
    try {
      const [m, l] = await Promise.all([adminApi.get('/admin/digital-products/meta'), adminApi.get('/admin/digital-products', { params: section === 'all' ? {} : { section } })]);
      setMeta(m.data);
      setItems(l.data.items);
    } catch (e) { toast.error(errMsg(e, 'Failed to load digital products')); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [section]);

  const createNew = async () => {
    const title = window.prompt('Resource title');
    if (!title?.trim()) return;
    try {
      const body = { title: title.trim() };
      if (section !== 'all') { body.sections = [section]; if (section === 'journal' || section === 'founder-insights') body.content_type = 'article'; }
      const r = await adminApi.post('/admin/digital-products', body);
      await load();
      setOpenId(r.data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const used = meta?.storage_used_bytes || 0;
  const quota = meta?.storage_quota_bytes || 1;
  return (
    <div className="px-6 lg:px-10 py-8" data-testid="dp-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl text-[#111]" style={serif} data-testid="dp-admin-title">Resources</h1>
          <p className="text-sm text-[#666] mt-1">Downloads and articles for all five Resources sections: Business Templates, AI Resources, Founder Insights, The Journal, and Digital Products.</p>
        </div>
        <div className="flex items-center gap-4">
          {meta && (
            <div className="text-right" data-testid="dp-admin-storage">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#888]">Storage {formatBytes(used)} / {formatBytes(quota)}</div>
              <div className="mt-1 w-40 h-[3px] bg-[#f1ece3] rounded-full"><div className="h-full bg-[#C8A15A] rounded-full" style={{ width: `${Math.min(100, (used / quota) * 100)}%` }} /></div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#888] mt-1">Stripe: {meta.stripe_mode}</div>
            </div>
          )}
          {tab === 'products' && (
            <button type="button" onClick={createNew} data-testid="dp-admin-new-product"
              className="text-xs tracking-[0.18em] uppercase px-5 py-2.5 bg-[#111] text-[#FAF8F5] rounded-full hover:bg-black">New Resource</button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <Tab active={tab === 'products'} onClick={() => setTab('products')} testid="dp-admin-tab-products">All Resources</Tab>
        <Tab active={tab === 'categories'} onClick={() => setTab('categories')} testid="dp-admin-tab-categories">Categories</Tab>
        <Tab active={tab === 'orders'} onClick={() => setTab('orders')} testid="dp-admin-tab-orders">Orders &amp; Downloads</Tab>
        <Tab active={tab === 'emails'} onClick={() => setTab('emails')} testid="dp-admin-tab-emails">Email Previews</Tab>
      </div>
      {tab === 'products' && (
        <>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="dp-admin-section-filter">
            {[['all', 'All Sections'], ...SECTION_OPTIONS].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setSection(k)} data-testid={`dp-admin-section-${k}`}
                className={`text-[11px] px-3 py-1 rounded-full border ${section === k ? 'bg-[#C8A15A] border-[#C8A15A] text-[#111]' : 'bg-white border-[#eae4d8] text-[#666]'}`}>{l}</button>
            ))}
          </div>
          <ProductsTable items={items} onOpen={setOpenId} />
        </>
      )}
      {tab === 'categories' && <CategoriesPanel categories={meta?.categories || []} onChange={load} />}
      {tab === 'orders' && <OrdersPanel products={items} />}
      {tab === 'emails' && <EmailPreviewPanel />}
      {openId && meta && <ProductEditor id={openId} meta={meta} onClose={() => setOpenId(null)} onSaved={load} />}
    </div>
  );
}
